import { TemplateApi } from 'marketplace/api/TemplateApi';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import React from 'react';
import { RamdaExtender } from 'utils/ramdaExtender';

import { FieldInput } from 'reactComponents/Forms/FieldInput';
import { Validation } from 'reactComponents/Forms/Validation/Validation';

const getImage = ({ images, templateJson }) => {
    const namerOrId = templateJson.parameters.image_id.default;
    return images.find((image) => [image.name, image.id].includes(namerOrId));
};

// also possible key_name_2, instance_type_3 etc
const FIRST_STEP_FIELDS = [
    'key_name',
    'instance_type', // flavor
    'image_id',
    'network_id',
    'volume_type',
    'volume_size',
    'availability_zone',
];

const SERVER_FIELDS_NAMES = [
    'instance_type',
    'volume_size',
    'volume_type',
    'availability_zone',
];

const _isExcluding = (val, key) =>
    R.any((excludingName) => key.includes(excludingName))(FIRST_STEP_FIELDS);

const _isHidden = R.propEq('hidden', true);

const _isUnknownType = (item) => {
    const KNOWN_TYPES = ['string', 'number'];

    if (!KNOWN_TYPES.includes(item.type)) {
        console.error(
            `unknown field type ${item.type} in template is ignored`,
            item,
        );
        return true;
    }
};

const _omitAdditionalParameters = RA.omitBy(
    R.anyPass([_isHidden, _isExcluding, _isUnknownType]),
);

const getEmptyHiddenParameters = (templateJson = {}) => {
    const { parameters = {} } = templateJson;
    const isEmpty = RamdaExtender.negateFunc(R.prop('default'));
    return R.pickBy(R.allPass([_isHidden, isEmpty]), parameters);
};

const hasAdditionalFields = (templateJson = {}) => {
    const { parameters = {} } = templateJson;
    return R.pipe(
        _omitAdditionalParameters,
        R.keys,
        RA.lengthGt(0),
    )(parameters);
};

const getAdditionalFieldsParams = (templateJson = {}) => {
    const { parameters = {} } = templateJson;

    const paramsByField = R.pipe(
        _omitAdditionalParameters,
        R.mapObjIndexed(_getInputFieldParams),
        R.values,
    )(parameters);

    return {
        fieldElements: paramsByField.map(
            ({ name, fieldComponent, attributes }) => {
                return React.createElement(fieldComponent, {
                    name,
                    key: name,
                    ...attributes,
                });
            },
        ),
        initialValues: _getInitialValues(paramsByField),
        validationSchema: _getValidationSchema(paramsByField),
    };
};

const PARAMETER_TYPE_TO_INPUT_TYPE = {
    string: 'text',
    number: 'number',
};

const _handleType = (type) => PARAMETER_TYPE_TO_INPUT_TYPE[type] || type;

const _getInputFieldParams = (item, key) => {
    return {
        name: key,
        attributes: {
            label: item.description,
            type: _handleType(item.type),
            ..._getConstraintsAttributes(item.constraints),
        },
        fieldComponent: FieldInput,
        initialValue: item.default || '',
        validation: _createValidation(item),
    };
};

const _getConstraintsAttributes = (constraints = []) => {
    const rangeObj = constraints.find(R.prop('range'));
    if (rangeObj) {
        return rangeObj.range;
    }

    const lengthObj = constraints.find(R.prop('length'));
    if (lengthObj) {
        return {
            maxLength: lengthObj.length.max,
        };
    }
};

const _createValidation = ({ constraints = [], type }) => {
    const TYPE_TO_VALIDATION = {
        string: Validation.Yup.string().required(),
        number: Validation.Yup.number().required(),
    };
    let validation = TYPE_TO_VALIDATION[type];

    constraints.forEach((constraint) => {
        switch (true) {
            case constraint.length != null:
                ['min', 'max'].forEach((key) => {
                    const value = constraint.length[key];
                    if (value != null) {
                        validation = validation[key](
                            value,
                            constraint.description,
                        );
                    }
                });
                break;

            case constraint.range != null:
                ['min', 'max'].forEach((key) => {
                    const value = constraint.range[key];
                    if (value != null) {
                        validation = validation[key](
                            value,
                            constraint.description,
                        );
                    }
                });
                break;

            case constraint.allowed_pattern != null:
                validation = validation.matches(
                    new RegExp(constraint.allowed_pattern),
                    constraint.description,
                );
                break;

            default:
                console.warn('unknown constraint type', constraint);
                break;
        }
    });

    return validation;
};

const _getInitialValues = (paramsByField) => {
    return paramsByField.reduce((acc, fieldParams) => {
        return {
            ...acc,
            [fieldParams.name]: fieldParams.initialValue || '',
        };
    }, {});
};

const _getValidationSchema = (paramsByField) => {
    const validations = paramsByField.reduce((acc, fieldParams) => {
        if (fieldParams.validation) {
            acc[fieldParams.name] = fieldParams.validation;
        }
        return acc;
    }, {});

    return Validation.createSchema(validations);
};

const getTemplate = R.prop('templateJson');
const getAdditionalTemplates = R.prop('additionalTemplates');

const _isNovaType = R.propEq('type', 'OS::Nova::Server');

const _filterByTypeNova = R.filter(_isNovaType);

const getResources = R.converge(
    (template, additionalTemplates) => {
        const baseTemplateResources = R.propOr({}, 'resources', template);

        const additionalTemplateResources = R.pipe(
            R.map(R.propOr({}, 'resources')),
        )(additionalTemplates);

        return R.mergeAll(
            additionalTemplateResources.concat(baseTemplateResources),
        );
    },
    [getTemplate, getAdditionalTemplates],
);

const getInstances = R.pipe(getResources, _filterByTypeNova);

const getInstancesKeys = R.pipe(getInstances, R.keys);

const getNumberOfInstances = R.pipe(getInstancesKeys, R.length);

const getParameters = R.pipe(getTemplate, R.propOr({}, 'parameters'));

const getDefaultFalvorName = R.pipe(
    getParameters,
    R.path(['instance_type', 'default']),
);

const _getServerFieldsData = (key, index, keys, parameters, instances) => {
    index++;

    const instance = instances[key];

    const instanceTitle = R.pathOr(key, ['metadata', 'description'])(instance);

    const addIndex = (name) => name + '_' + index;
    const handleName = index === 1 ? R.identity : addIndex;

    const pickParameters = R.partialRight(R.pick, [parameters]);

    const getInitialValue = R.prop('default');

    const uniteData = (fieldNames, initialValues) => ({
        initialValues,
        instanceTitle,
        fieldNames,
    });

    const boot = R.pipe(
        R.map(handleName),
        pickParameters,
        R.converge(uniteData, [R.keys, R.map(getInitialValue)]),
    );

    return boot(SERVER_FIELDS_NAMES);
};

const getFieldsOfServers = R.converge(
    (keys, instances, parameters) => {
        const getServerFieldsDataBound = R.partialRight(_getServerFieldsData, [
            parameters,
            instances,
        ]);
        return keys.map(getServerFieldsDataBound);
    },
    [getInstancesKeys, getInstances, getParameters],
);

const getServerFieldNames = R.pipe(getFieldsOfServers, R.pluck('fieldNames'));
const getServerFieldNamesMerged = R.pipe(getServerFieldNames, R.flatten);

const getTemplateMetadata = R.prop('metadata');

const getMetadata = R.pipe(getTemplate, getTemplateMetadata);

const getLogoUrl = TemplateApi.makeLogoUrl;

export const AppConfigurationUtils = {
    getImage,
    hasAdditionalFields,
    getAdditionalFieldsParams,
    getEmptyHiddenParameters,
    getDefaultFalvorName,
    getMetadata,
    getNumberOfInstances,
    getLogoUrl,
    getTemplateMetadata,
    getFieldsOfServers,
    getServerFieldNames,
    getServerFieldNamesMerged,
};
